import React, { useMemo } from 'react';
import { createCnGoodFilterOption } from 'modules/Common/utils/cnGoodFilterOption';
import productionMethods from '../data/ProdMethods.json';
import { CgFilterSelect } from 'modules/Common/ui/CgFilterDropdown';
import { useTranslation } from 'react-i18next';
import { resolveProductionMethods } from '../utils/resolveProductionMethods';

// Controller-compatible field wrapper for the CN Good picker, on the
// standard CgFilterSelect. Receives {field, fieldState, id} injected by
// FormField's cloneElement, plus explicit props: cnGoods,
// setCategoryProductionMethods, setValue, disabled.
export default function CnGoodField({
  field,
  fieldState,
  id,
  cnGoods,
  setCategoryProductionMethods,
  setValue,
  disabled,
}) {
  const { t } = useTranslation('installations');
  const options = useMemo(
    () =>
      (cnGoods || []).map((good) => ({
        value: good._id,
        label: [
          good._id,
          good.name,
          good.aggregatedGoodsCategoryLabel ?? good.aggregatedGoodsCategory,
        ]
          .filter(Boolean)
          .join(' - '),
        aggregatedGoodsCategory: good.aggregatedGoodsCategory,
      })),
    [cnGoods],
  );

  // field.value may hold either the _id or the raw cnCode — normalize to
  // the option value so the trigger renders the selected label.
  const selectedValue = useMemo(() => {
    if (!field.value || !cnGoods?.length) return null;
    const good = cnGoods.find((g) => g._id === field.value || g.cnCode === field.value);
    return good?._id ?? null;
  }, [field.value, cnGoods]);

  const handleChange = (val) => {
    field.onChange(val ?? '');
    if (!val) return;
    const option = options.find((o) => o.value === val);
    if (!option) return;
    // Shared resolver: trims the category (the catalog stores "Electricity "
    // with a trailing space) and returns a sorted copy rather than sorting
    // ProdMethods.json's array in place, which reordered it for every other
    // consumer in the session.
    const categoryMethods = resolveProductionMethods(
      { aggregatedGoodsCategory: option.aggregatedGoodsCategory, cnCode: val },
      cnGoods,
      productionMethods,
    );
    setCategoryProductionMethods(categoryMethods);
    setValue(
      'productionMethods',
      [
        {
          methodCode: categoryMethods[0]?.id ?? '',
          name: categoryMethods[0]?.name ?? '',
          steelMill: '',
          directParameters: [],
        },
      ],
      { shouldValidate: false },
    );
  };

  return (
    <CgFilterSelect
      options={options}
      value={selectedValue}
      onChange={handleChange}
      disabled={disabled}
      placeholder={t('fields.selectCnGoodReq', 'Select a CN Good *')}
      filterOption={createCnGoodFilterOption}
      searchable
      isClearable={false}
      error={Boolean(fieldState?.error)}
    />
  );
}
